import React from 'react';

import { TableCell, TableHeaderCell } from '@hcs/design-system';
import { TableCellProps, TableHeaderCellProps } from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import { formatDateShort } from '@hcs/utils';

const FIELD = PropertyStateFields.currentStatusDateRental;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Rental Market Price Date';
const labelShort = 'Rental $ Date';
const propertyStatePath: PropertyStatePaths =
  '/complexFieldsRental/currentStatusDate';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  }
  return propertyState?.complexFieldsRental?.currentStatusDate;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatDateShort(getValue(propertyStateArgs));

// For displaying multiple fields in a single line
const labelMicro: FieldConfig['labelMicro'] = 'Date';
const formatValueShort: FieldConfig['formatValueShort'] = (propertyStateArgs) =>
  formatDateShort(getValue(propertyStateArgs));

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  `${formatValue(propertyStateArgs)}`;

// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display propertyStateArgs={propertyStateArgs} />
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

export const CURRENT_STATUS_DATE_RENTAL_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  formatInline,
  labelMicro,
  formatValueShort,
  HeaderCell,
  ContentCell,
  Display,
};
